---
title: "What Is Trading? Markets, Assets and How Prices Move"
slug: what-is-trading
shortTitle: "What Is Trading?"
description: "A plain-English starting point: what trading is, how it differs from investing, which markets you can trade, and what actually makes prices rise and fall."
level: beginner
category: markets
course: beginner
order: 1
topics: [macro-news, cfds]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/what-is-trading.svg
faqs:
  - q: "What is the difference between trading and investing?"
    a: "Investing usually means buying an asset and holding it for years because you believe in its long-term value. Trading means opening and closing positions over shorter periods, from minutes to weeks, to act on price movements in either direction."
  - q: "Do I need a lot of money to start trading?"
    a: "No. You can start with a small amount, and on a CFD platform you can open positions much smaller than one whole share or coin. What matters more is only trading money you can afford to lose and keeping each position small relative to your account."
  - q: "Why does a price move when nothing seems to have happened?"
    a: "Prices move whenever buyers or sellers become more eager than the other side. That can be caused by large orders, shifting expectations, news in another market, or simply traders closing positions, so not every move has a headline behind it."
  - q: "Which market is best for beginners?"
    a: "There is no single best market. Many beginners start by following just one or two markets, often ones they already hear about in the news, so they can learn how those markets behave before adding others."
  - q: "Can I practice without risking real money?"
    a: "Yes, if a demo account is available to you. On the platform you can request one with the Demo button on desktop; it gives you a virtual balance so you can practice placing and managing trades."
---

Every price you see on a screen, from Bitcoin to gold to a share of a large company, is the result of people buying and selling. **Trading** means taking part in that process with a specific goal: to profit from changes in price over minutes, days or weeks, rather than holding an asset for many years.

This first lesson of the Beginner Path covers the ground that everything else builds on. You will learn what a market is, which kinds of assets you can trade on {{BRAND}}, what actually makes prices move, and what separates a planned trade from a bet.

<CTA label="Explore the markets" to="/markets" />

<KeyTakeaways>

- **Trading is about price changes.** A trader aims to profit from the difference between the price a position opens at and the price it closes at, in either direction.
- **A price is an agreement, not a fact.** It is the latest level where a buyer and a seller were both willing to deal, and it changes as soon as that balance shifts.
- **Different markets move for different reasons.** Interest rates, company earnings, supply shocks and crypto news each matter more to some assets than to others.
- **Risk comes before reward.** Many traders lose money. Deciding how much you can lose on a trade before you open it matters more than guessing where the price goes next.

</KeyTakeaways>

## Trading versus investing

Both traders and investors buy and sell assets, but they think about time and risk differently.

An **investor** buys something because they believe it will be worth more in several years. They often ignore day-to-day swings and may hold through large drops.

A **trader** focuses on how the price is likely to behave over a shorter period. A trade has a reason to open, a point where it is clearly wrong, and a plan for taking profit. Traders can also act on falling prices by going short, which you will meet in [Long vs Short](/academy/article/long-vs-short).

Neither approach is better in general; this path focuses on the trader's toolkit.

## What a market is

A **market** is any place where buyers and sellers meet to agree on a price. Some markets run through a central exchange, such as a stock exchange or a crypto exchange, where every order sits in a shared list called an **order book**. Others, like the main currency market, run between banks and dealers directly.

Two ideas will come up again and again:

- **Liquidity** is how easily you can buy or sell without moving the price much. A busy market with many participants is liquid; a quiet one is thin.
- **Two prices.** At any moment there is a price buyers are willing to pay and a slightly higher price sellers are asking for. You will learn why this gap matters in [Bid, Ask, Spread and Why They Matter](/academy/article/bid-ask-spread).

## The five kinds of markets on {{BRAND}}

{{BRAND}} offers five asset classes. Every one of them is traded as a CFD, a contract on the price that is settled in USDT, so you never take delivery of the coin, metal or share. [What Is a CFD?](/academy/article/what-is-a-cfd) explains how that works.

<CompareTable caption="Asset classes and what tends to move them">

| Asset class | Examples on {{BRAND}} | Often moved by |
| --- | --- | --- |
| Crypto | BTC/USDT, ETH/USDT, SOL/USDT | Adoption, regulation, liquidity, overall risk appetite |
| Forex | EUR/USD | Interest-rate decisions, inflation, economic data |
| Commodities | GOLD/USDT, OIL/USDT, SILVER/USDT | Physical supply and demand, the US dollar, geopolitics |
| Stocks | AAPL, TSLA, NVDA | Company earnings, outlook, sector news |
| Indices | SPX500, NAS100, GER40 | The economy, interest rates, results from large companies |

</CompareTable>

Markets also keep different hours. Crypto trades around the clock, every day of the week. Forex runs around the clock on weekdays. Stocks and indices follow the sessions of their home exchanges, and commodities have their own weekday sessions. Before you trade a market for the first time, check when it is open.

{/* TODO: verify with platform — where trading hours for each market are shown, and whether stock and index CFDs offer extended hours. */}

## How prices actually move

At its simplest, a price moves because one side of the market is more eager than the other.

Picture a market where buyers are offering 99 and sellers are asking 100. If a wave of buyers decides they are happy to pay 100, they use up the sellers at that level, and the next seller is asking 101. The price ticks up. If sellers become impatient and accept the buyers' 99, the price ticks down.

That is all a chart is: a record of where those deals happened.

### Prices follow expectations

What makes buyers or sellers more eager is usually a change in **expectations**. Prices often move when news changes what people believe about the future, not when the event itself arrives. If a rate cut is widely expected, much of its effect may already be "priced in" by the day it happens. A surprise, in either direction, tends to move prices far more.

Common sources of surprise include:

1. **Economic data and central banks.** Inflation figures, jobs reports and interest-rate decisions affect currencies, indices, gold and often crypto.
2. **Company news.** Quarterly earnings and changes to a company's outlook can move a stock sharply in minutes.
3. **Supply shocks.** Production decisions by oil exporters, mine closures or poor harvests change the balance for commodities.
4. **Crypto-specific events.** Regulation, exchange failures, network upgrades and large flows into or out of crypto products.
5. **Sentiment.** Fear and greed spread quickly, and crowded positions can unwind suddenly.

[Fundamental Drivers](/academy/article/fundamental-drivers) looks at each of these in more depth.

### Volatility: how far and how fast

**Volatility** describes how much a price tends to move. Two positions of the same size can carry very different risk if one market moves ten times as much as the other.

<Example title="Same size, different volatility (illustrative prices)">

You hold two positions, each worth 1,000 USDT.

- An index moves 1% in a day: 1,000 × 1% = **10 USDT** of profit or loss.
- A cryptocurrency moves 6% in the same day: 1,000 × 6% = **60 USDT** of profit or loss.

Same position value, six times the swing. Volatility is one reason to size positions differently from market to market.

</Example>

## A trade in numbers

Here is the basic arithmetic you will use in every lesson that follows.

<Example title="A gold position (illustrative prices)">

Gold is at 2,400 USDT per ounce. You buy a position worth 2,400 USDT, which is 1 ounce.

- Gold rises to 2,436: (2,436 − 2,400) × 1 = **+36 USDT**.
- Gold falls to 2,376: (2,376 − 2,400) × 1 = **−24 USDT**.

</Example>

Real results are slightly worse, because every trade carries costs: the gap between the buying and selling price, a trade fee ({{TRADE_FEE}}), and on some markets a small daily holding charge called a swap.

<Warning>

Trading CFDs is risky, and many traders lose money. Leverage, which you will learn about later in this path, lets you open larger positions than your deposit, so losses can grow quickly. Only trade with money you can afford to lose.

</Warning>

## What separates trading from gambling

A bet relies on hope. A trade relies on a plan you can write down before you click. At a minimum, that plan answers four questions:

1. **Why am I entering?** A reason you could explain to someone else.
2. **Where am I wrong?** A price where the idea no longer holds, which becomes your stop loss.
3. **Where will I take profit?** A target, or a rule for when to exit.
4. **How much will I lose if I am wrong?** A loss you can accept, worked out in USDT before you open the trade.

<Steps>
<Step title="Learn the basics first">

Work through this Beginner Path in order; each lesson builds on the one before.

</Step>
<Step title="Practice without real money">

If a demo account is available to you, request one with the **Demo** button in the desktop top bar. It comes with {{DEMO_BALANCE}} to practice with.

{/* TODO: verify with platform — demo account availability and how it is requested. */}

</Step>
<Step title="Start small and keep notes">

With real funds, keep positions small and note why you entered, where you exited and what you learned.

</Step>
</Steps>

<ProTip>

You do not need to watch the screen all day. Set a price alert with the bell on any market row, or under Account → Alerts, and let the platform tell you when a level you care about is reached.

</ProTip>

## Where to see all this on {{BRAND}}

The **Markets** page lists every instrument with its live price and recent change, along with an economic calendar and market news. Choose a market and you land on its trade screen, for example `/trade/BTC` or `/trade/GOLD`, with the chart on the left and the order panel on the right.

<Screenshot name="markets-page-overview" alt="The Markets page listing instruments with prices, 24h changes, trends and signals, next to market movers and the opportunity scanner" caption="The Markets page: every instrument, with movers, the opportunity scanner and, further down, the economic calendar." />

## Conclusion

Trading is the business of acting on price changes, with a plan for being right and for being wrong. A price is simply the latest agreement between buyers and sellers, and it moves when expectations shift. Each asset class responds to its own drivers and moves by different amounts. Treat risk as the first question rather than the last, and the next lessons on crypto, USDT and CFDs will give you the tools to trade with care.
